import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import Log from "@/Logger";
import { generateRequestId } from "@/feature/api-client";
import type { FormPage, EWBCIItpcmdResponse, ItpcmdPatchFormPageResponse, FormField } from "@/feature/ewbci";

import { DraftStatus, HistoryType } from "../types";
import type { Draft, DraftMap, HistoryItem } from "../types";
import useFieldUpdateHistory from "./useFieldUpdateHistory";
import { compkey, errmsg, hashJson } from "../utils";

type State = {
    pageId?: string;
    drafts: DraftMap;
    addHistory: (items: HistoryItem | HistoryItem[]) => void;
    requestId?: string;
    retryCount: number;
};
const initState = (addHistory: (items: HistoryItem | HistoryItem[]) => void): State => (
    {
        drafts: {},
        addHistory,
        retryCount: 0,
    }
);

export type FieldUpdateManagerHooks = {
    /** ドラフト状態 */
    drafts: DraftMap;
    /** フォームフィールドにフォーカスが入ったことを通知する */
    notifyFocusIn: (key: string) => void;
    /** フォームフィールドからフォーカスが外れたことを通知する */
    notifyFocusOut: (key: string, value?: string) => void;
    /** eWBから受信したフォームの更新を適用する */
    notifyRemoteUpdate: (form: Record<string, string>, timestamp: Date) => void;
    /** 競合を解決する */
    resolveConflict: (key: string, value: string) => void;
    /** フィールドの更新履歴 */
    history: HistoryItem[];
    /** 履歴をクリア */
    clearHistory: () => void;
    /** 【テストの場合のみ】ページハッシュ値 */
    pageHash?: string;
}
type Action =
    | { type: "init", pageId?: string, fields?: FormField[], initialForm?: { form: Record<string, string>, timestamp?: Date } }
    | { type: "focusIn", key: string }
    | { type: "focusOut", key: string, value?: string }
    | { type: "fireResendEvent" }
    | { type: "sendRequest", pageId: string }
    | { type: "receiveResponse", pageId: string, requestId: string, result: Record<string, string>, responsedAt: Date, callback: () => void }
    | { type: "failedToRequest", pageId: string, requestId: string, callback: () => void }
    | { type: "resolveConflict", key: string, value: string }
    | { type: "remoteUpdate", form: Record<string, string>, timestamp: Date };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        // フォームフィールドのドラフト状態を初期化
        case "init": return onInit(state, action.pageId, action.fields, action.initialForm);
        // フォーカスインしたフィールドの状態をEditingに変更
        case "focusIn": return onFocusIn(state, action.key);
        // フォーカスアウトしたフィールドの状態をIdleまたはUpdatedに変更
        case "focusOut": return onFocusOut(state, action.key, action.value);
        // 編集リクエスト送信: 指定されたキーのフィールドの状態をSendingに変更し、requestIdを設定
        case "sendRequest": return onSendRequest(state, action.pageId);
        // 送信再試行イベント発火: 直近のエラー発生時刻をクリアし、再送信可能にする
        case "fireResendEvent": return onFireResendEvent(state);
        // 編集レスポンス受信（成功）: 指定されたキーのフィールドの状態をIdleまたはDirtyまたはConflictに変更し、requestIdをクリア
        case "receiveResponse": return onResponseReceived(state, action.pageId, action.requestId, action.result, action.responsedAt, action.callback);
        // 編集リクエスト送信失敗: 指定されたキーのフィールドの状態をDirtyに変更し、requestIdをクリア
        case "failedToRequest": return onRequestFailed(state, action.pageId, action.requestId, action.callback);
        // 競合を解決する
        case "resolveConflict": return onResolveConflict(state, action.key, action.value);
        // eWBの更新を適用する
        case "remoteUpdate": return onRemoteUpdate(state, action.form, action.timestamp);
        // その他
        /* istanbul ignore next: TypeScript型安全性により到達不可 */
        default: return state;
    }
}

/** フォームフィールドのドラフト状態を初期化 */
function onInit(state: State, pageId?: string, fields?: FormField[], initialForm?: { form: Record<string, string>, timestamp?: Date }) {
    const logger = Log.getLogger();
    const { form, timestamp } = initialForm ?? {};

    logger.ui.info('useFieldUpdateManager', 'reducer', `init for page: ${pageId ?? '<none>'}, ${fields?.length ?? 0} fields. ${Object.keys(form ?? {}).length > 0 ? `with initial form(${Object.keys(form!).length}), at ${timestamp?.toISOString()}` : 'no initial form'}`);

    return {
        ...state,
        pageId,
        drafts: fields?.map(field => { // 新しいページなら初期化
            const initialValue = form ? form[field.key] : undefined;
            return {
                key: field.key,
                status: initialValue !== undefined ? DraftStatus.Idle : DraftStatus.None,
                local: initialValue,
                remote: initialValue,
                updatedAt: timestamp,
            };
        }).reduce((drafts, draft) => ({
            ...drafts,
            [draft.key]: draft
        }), {}) ?? {},
    };
}

/** フォーカスインしたフィールドの状態をEditingに変更 */
function onFocusIn(state: State, key: string): State {
    const logger = Log.getLogger();
    const drafts = state.drafts;
    const f = drafts[key];
    if (!f) {
        logger.ui.error('useFieldUpdateManager', 'reducer', `フォーカスイン通知で不明なフィールドキー: ${key}`);
        return state; // フィールドが存在しない場合は無視
    } else if (f.status !== DraftStatus.Idle &&
        f.status !== DraftStatus.Updated &&
        f.status !== DraftStatus.Dirty &&
        f.status !== DraftStatus.Editing) {// Editingはバリデーションエラー時に再入するため
        logger.ui.error('useFieldUpdateManager', 'reducer', `フォーカスイン通知で不適切な状態遷移: ${key} ${f.status}`);
        return state; // 不適切な状態遷移は無視
    }
    return {
        ...state,
        drafts: {
            ...drafts,
            [key]: {
                ...f,
                status: DraftStatus.Editing,
                errorAt: undefined, // フォーカスインしたのでエラー状態をクリア
            }
        }
    };
}

/**
 *  フォーカスアウトしたフィールドの状態をIdleまたはUpdatedに変更
 * 
 * Pendingされた更新があれば適用し、最終的な状態を判定（Idle|Updated|Dirty|Conflict）
 */
function onFocusOut(state: State, key: string, value?: string): State {
    const logger = Log.getLogger();
    const drafts = state.drafts;
    let draft = drafts[key];
    if (!draft) {
        logger.ui.error('useFieldUpdateManager', 'reducer', `フォーカスアウト通知で不明なフィールドキー: ${key}`);
        return state; // フィールドが存在しない場合は無視
    } else if (draft.status !== DraftStatus.Editing) {
        logger.ui.error('useFieldUpdateManager', 'reducer', `フォーカスアウト通知で不適切な状態遷移: ${key} ${draft.status}`);
        return state; // 不適切な状態遷移は無視
    }

    if (value !== undefined && draft.local !== value) {
        // 変更あり
        draft = {
            ...draft,
            // eWBの値と異なればDirtyに、同じならIdleに変更
            status: draft.remote !== value ? DraftStatus.Dirty : DraftStatus.Idle,
            local: value,
        };
        // TODO: 送信待ち/キャンセルの履歴を追加？
    } else {
        // 変更なし(状態の更新のみ)
        draft = {
            ...draft,
            // eWBの値と異なればDirtyに、同じならIdleに変更
            status: draft.remote !== draft.local ? DraftStatus.Dirty : DraftStatus.Idle,
        };
    }

    // 保留中の更新があれば適用する
    let history: HistoryItem[];
    [, draft, history] = applyPendingUpdates(draft);

    // 履歴に追加
    if (history.length > 0) state.addHistory(history);

    return { ...state, drafts: { ...drafts, [key]: draft } };
}

/** 編集リクエスト送信開始: Dirtyフィールドの状態をSendingに変更し、requestIdを設定 */
function onSendRequest(state: State, pageId: string): State {
    const logger = Log.getLogger();
    if (state.pageId !== pageId) {
        logger.ui.error('useFieldUpdateManager', 'onSendRequest', `send request for invalid page: [${pageId}] current=${state.pageId}`);
        return state; // 異なるページは無視
    }
    const drafts = state.drafts;
    // Dirtyな全フィールドを取得し、Sendingに変更する
    const updated = Object.entries(drafts)
        .reduce((m, [key, draft]) => (draft.status !== DraftStatus.Dirty)
            ? m
            : {
                ...m,
                [key]: {
                    ...draft,
                    status: DraftStatus.Sending,
                    errorAt: undefined, // 送信開始したのでエラー状態をクリア
                }
            }, {} as DraftMap);

    // 更新がなければそのまま返す
    if (Object.entries(updated).length === 0) {
        logger.ui.warn('useFieldUpdateManager', 'onSendRequest', `no update field`);
        return state;
    }

    // リクエストIDを発行
    const requestId = generateRequestId();

    // ログ出力
    logger.ui.info('useFieldUpdateManager', 'onSendRequest', `[${requestId}] update ${Object.keys(updated).length} fields on ${pageId}`);

    // TODO: 送信中の履歴を追加

    // 更新があればマージ(上書き)して返す
    return { ...state, drafts: { ...drafts, ...updated }, requestId };
}

/** 送信再試行イベント発火: 再送信のための、DraftMapの再評価をキックする */
function onFireResendEvent(state: State): State {
    const logger = Log.getLogger();
    let retryCount = state.retryCount + 1;
    logger.ui.info('useFieldUpdateManager', 'onFireResendEvent', `fire resend event (retryCount=${retryCount})`);
    return { ...state, retryCount: state.retryCount + 1 };
}

/** 編集レスポンス受信: 指定されたキーのフィールドの状態をIdleまたはConflictに変更し、requestIdをクリア */
function onResponseReceived(state: State, pageId: string, requestId: string, result: Record<string, string>, responsedAt: Date, callback: () => void): State {
    const logger = Log.getLogger();
    if (state.pageId !== pageId || state.requestId !== requestId) {
        logger.ui.error('useFieldUpdateManager', 'onResponseReceived', `receive response of invalid request: [${pageId}] ${requestId}`);
        return state; // 無効になったリクエストは無視（別のリクエストの状態が記録されている可能性があるのでいじらない）
    }
    const drafts = state.drafts;
    const updated: DraftMap = {};
    const history: HistoryItem[] = [];
    // レスポンスの内容を反映する
    Object.entries(result).forEach(([key, value]) => {
        let f = drafts[key];
        if (!f) {
            logger.ui.error('useFieldUpdateManager', 'onResponseReceived', `不明なフィールドキー: ${key}`);
            return;
        }

        if (f.updatedAt && f.updatedAt > responsedAt) {
            logger.ui.info('useFieldUpdateManager', 'onResponseReceived', `古いレスポンスを無視: ${key} ${f.updatedAt.toISOString()} > ${responsedAt.toISOString()}`);
            // 古いレスポンスは失敗扱いとしてステータスのみ更新する
            f = {
                ...f,
                status: (f.local === f.remote) ? DraftStatus.Idle : DraftStatus.Dirty,
            };
            // 履歴に追加
            history.push({ type: HistoryType.Ignored, fieldName: f.key, prev: f.remote, next: value, timestamp: responsedAt });

        } else {
            // Draftの状態を更新
            let status = f.status;
            logger.ui.error('useFieldUpdateManager', 'onResponseReceived', `${key}<${f.status}> ${f.local}(${f.remote}) -> ${value}`);
            if (f.local === value) { // 送信した値と同じなら反映（Idle）
                status = DraftStatus.Idle;
                // 履歴に追加
                history.push({ type: HistoryType.Send, fieldName: f.key, prev: f.remote, next: value, timestamp: responsedAt });
            } else if (f.remote === value) { // eWBの値と同じなら失敗（Dirty）
                status = DraftStatus.Dirty;
                // TODO: 送信失敗の履歴を追加
                // history.push({ type: HistoryType.Send, fieldName: f.key, prev: f.remote, next: value, timestamp: responsedAt });
            } else { // どちらとも異なるなら競合（Conflict）
                status = DraftStatus.Conflict;
                // 履歴に追加
                history.push({ type: HistoryType.Conflicted, fieldName: f.key, prev: value, next: f.local ?? '', timestamp: responsedAt });
            }
            f = {
                ...f,
                status,
                remote: value,
                updatedAt: responsedAt,
            };
        }

        // 保留中の更新があれば適用する
        let h: HistoryItem[];
        [, f, h] = applyPendingUpdates(f);
        // 更新配列に追加
        updated[key] = f;
        // 履歴に追加
        history.push(...h);
    });
    // 漏れをチェック
    Object.entries(drafts).forEach(([key, draft]) => {
        if (draft.status !== DraftStatus.Sending) return; // Sending以外は無視
        if (updated[key]) return; // 既に処理済み

        // Draftの状態を更新
        logger.ui.error('useFieldUpdateManager', 'onResponseReceived', `${key} is missing in results.`);

        // TODO: 送信失敗の履歴を追加
        // history.push({ type: HistoryType.Send, fieldName: f.key, prev: f.remote, next: value, timestamp: responsedAt });

        // Dirtyに戻す
        draft = {
            ...draft,
            status: DraftStatus.Dirty,
        };

        // 保留中の更新があれば適用する
        let h: HistoryItem[];
        [, draft, h] = applyPendingUpdates(draft);
        // 更新配列に追加
        updated[key] = draft;
        // 履歴に追加
        history.push(...h);
    });

    // 履歴に追加
    if (history.length > 0) state.addHistory(history);

    callback(); // コールバックを呼び出す

    // 更新があればマージ(上書き)して返す
    return {
        ...state,
        drafts: (Object.entries(updated).length === 0)
            ? drafts
            : { ...drafts, ...updated },
        requestId: undefined
    };
}

/** 編集リクエスト送信失敗: 指定されたキーのフィールドの状態をIdleまたはConflictに変更し、requestIdをクリア */
function onRequestFailed(state: State, pageId: string, requestId: string, callback: () => void): State {
    const logger = Log.getLogger();
    if (state.pageId !== pageId || state.requestId !== requestId) {
        logger.ui.error('useFieldUpdateManager', 'onRequestFailed', `receive response of invalid request: [${pageId}] ${requestId}`);
        return state; // 無効になったリクエストは無視（別のリクエストの状態が記録されている可能性があるのでいじらない）
    }
    const drafts = state.drafts;
    const updated: DraftMap = {};
    const history: HistoryItem[] = [];
    const errorAt = Date.now();
    // 送信中状態のフィールドを探す
    Object.entries(drafts).forEach(([key, f]) => {
        if (f.status !== DraftStatus.Sending) return;  // 無視
        // Draftの状態を更新
        f = { ...f, status: DraftStatus.Dirty, errorAt };
        // TODO: 送信失敗の履歴を追加

        // 保留中の更新があれば適用する
        let h: HistoryItem[];
        [, f, h] = applyPendingUpdates(f);
        // 更新配列に追加
        updated[key] = f;
        // 履歴に追加
        history.push(...h);
    });

    callback(); // コールバックを呼び出す

    // 更新があればマージ(上書き)して返す
    return {
        ...state,
        drafts: (Object.entries(updated).length === 0)
            ? drafts
            : { ...drafts, ...updated },
        requestId: undefined
    };
}

/** 保留中の更新を適用し、最終的な状態を判定する */
function applyPendingUpdates(orig: Draft): [boolean, Draft, HistoryItem[]] {
    const logger = Log.getLogger();
    const history: HistoryItem[] = [];
    const draft = { ...orig }; // ここでコピーしないと参照が変わらない
    if (!draft.pending || draft.pending.length === 0) return [false, orig, history];
    logger.ui.warn('useFieldUpdateManager', 'applyPendingUpdates', `${draft.key} has ${draft.pending.length} pending updates.`);
    // 保留中の更新があれば、最終的にどうなるかで判定
    const latest = draft.pending.reduce((p, n) => {
        const latest = (!p || p.timestamp < n.timestamp) ? n : p;
        const ignored = (!p || p.timestamp < n.timestamp) ? p : n;
        // 履歴に追加
        history.push({ type: HistoryType.Ignored, fieldName: draft.key, prev: draft.remote, next: ignored.value, timestamp: ignored.timestamp });
        return latest;
    });
    if (!draft.updatedAt || draft.updatedAt < latest.timestamp) {
        // 履歴に追加
        history.push({ type: HistoryType.Receive, fieldName: draft.key, prev: draft.remote, next: latest.value, timestamp: latest.timestamp });
        // より新しい情報なら反映する
        draft.updatedAt = latest.timestamp;
        if (draft.remote !== latest.value) {
            // eWBの値が更新される場合
            draft.remote = latest.value;
            // 更新後に比較し、状態を遷移させる
            switch (draft.status) {
                case DraftStatus.Idle:
                    draft.status = DraftStatus.Updated; // 更新ありに変更
                    draft.local = latest.value; // ローカルも更新
                    break;
                case DraftStatus.Dirty:
                    if (draft.remote === draft.local)
                        draft.status = DraftStatus.Idle; // 編集した値と同じなら反映（Idle）
                    else
                        draft.status = DraftStatus.Conflict; // 編集した値と異なるなら競合（Conflict）
                    break;
                /* istanbul ignore next : Conflictでこの関数に入ってくることがない */
                case DraftStatus.Conflict:
                    logger.ui.warn('useFieldUpdateManager', 'applyPendingUpdates', `不適切な状態遷移: ${draft.key} Conflict`);
                    if (draft.remote === draft.local)
                        draft.status = DraftStatus.Idle; // 編集した値と同じならeWBから競合解決（Idle）
                    else
                        draft.status = DraftStatus.Conflict; // 編集した値と異なるなら再競合（Conflict）
                    break;
            }
        }
    } else {
        logger.ui.debug('useFieldUpdateManager', 'applyPendingUpdates', `古い更新を無視: ${draft.key} ${draft.updatedAt.toISOString()} >= ${latest.timestamp.toISOString()}`);
        // 履歴に追加
        history.push({ type: HistoryType.Ignored, fieldName: draft.key, prev: draft.remote, next: latest.value, timestamp: latest.timestamp });
    }
    delete draft.pending; // 保留中の更新をクリア

    return [true, draft, history];
}

/** 競合を解決する */
function onResolveConflict(state: State, key: string, value: string): State {
    const logger = Log.getLogger();
    const drafts = state.drafts;
    const f = drafts[key];
    if (!f) {
        logger.ui.error('useFieldUpdateManager', 'resolveConflict', `不明なフィールドキー: ${key}`);
        return state; // フィールドが存在しない場合は無視
    } else if (f.status !== DraftStatus.Conflict) {
        logger.ui.error('useFieldUpdateManager', 'resolveConflict', `不適切な状態遷移: ${key} ${f.status}`);
        return state; // 不適切な状態遷移は無視
    }

    // TODO: 履歴に追加
    // state.addHistory({ type: HistoryType.Resolved, fieldName: key, prev: f.remote, next: value, timestamp: new Date() });

    return {
        ...state,
        drafts: {
            ...drafts,
            [key]: {
                ...f,
                // 解決後の値がeWBの値と同じならキャンセル（Idle）, 異なるなら再送（Dirty）
                status: (f.remote === value ? DraftStatus.Idle : DraftStatus.Dirty),
                local: value,
            }
        }
    };
}

/** eWBの更新を適用する */
function onRemoteUpdate(state: State, form: Record<string, string>, timestamp: Date): State {
    const logger = Log.getLogger();
    const drafts = state.drafts;
    let updated: DraftMap = {};
    const history: HistoryItem[] = [];
    // logger.ui.info('useFieldUpdateManager', 'onRemoteUpdate', `${state.pageId}`);

    Object.entries(form).forEach(([key, value]) => {
        let f = drafts[key];
        if (!f) {
            logger.ui.error('useFieldUpdateManager', 'onRemoteUpdate', `不明なフィールドキー: ${key}`);
            return; // フィールドが存在しない場合は無視
        }
        if (f.updatedAt && f.updatedAt >= timestamp) {
            logger.ui.info('useFieldUpdateManager', 'onRemoteUpdate', `古い更新を無視: ${key} ${f.updatedAt.toISOString()} >= ${timestamp.toISOString()}`);
            history.push({ type: HistoryType.Ignored, fieldName: key, next: value, timestamp });
            return; // 古い更新は無視
        }
        if (f.status === DraftStatus.Editing || f.status === DraftStatus.Sending) {
            logger.ui.info('useFieldUpdateManager', 'onRemoteUpdate', `編集中または送信中の更新は保留: ${key} ${f.status}`);
            // 編集中または送信中の更新は保留
            if (!f.pending) f.pending = [];
            f.pending.push({ value, timestamp });
            updated[key] = f;
            return;
        }

        // 履歴に追加(初回以外)
        if (f.status !== DraftStatus.None && f.remote !== value)
            history.push({ type: HistoryType.Receive, fieldName: key, prev: f.remote, next: value, timestamp });

        // Draftの状態を更新（必要あればlocalも更新）
        switch (f.status) {
            case DraftStatus.None:
                f.status = DraftStatus.Idle;
                f.local = value; // 初期値として設定
                break;
            case DraftStatus.Idle:
                if (f.remote !== value) {
                    f.status = DraftStatus.Updated; // 更新あり
                    f.local = value; // ローカルも更新
                }
                // else そのまま
                break;
            case DraftStatus.Updated:
                f.local = value; // ローカルも更新
                break; // そのまま
            case DraftStatus.Dirty:
                if (f.local === value)
                    f.status = DraftStatus.Idle; // 反映済み
                else
                    f.status = DraftStatus.Conflict; // 競合

                break;
            case DraftStatus.Conflict:
                if (f.local === value)
                    f.status = DraftStatus.Idle; // eWBから競合解決
                // else そのまま
                break;

            /* istanbul ignore next : 上記以外の状態でここに来ることはない */
            // case DraftStatus.Editing:
            // case DraftStatus.Sending:
            default:
                logger.ui.error('useFieldUpdateManager', 'onRemoteUpdate', `不適切な状態遷移: ${key} ${f.status}`);
                return; // 不適切な状態遷移は無視
        }
        f.remote = value;
        f.updatedAt = timestamp;
        // 更新配列に追加
        updated[key] = f;
    });

    // 履歴を追加
    if (history.length > 0) state.addHistory(history);

    // 更新がなければそのまま返す
    if (Object.entries(updated).length === 0) return state;
    // 更新があればマージ(上書き)して返す
    return { ...state, drafts: { ...drafts, ...updated } };
}

export function hasConflictFields(drafts: DraftMap): boolean {
    return Object.values(drafts).some(draft => draft.status === DraftStatus.Conflict);
}

export function hasEditingFields(drafts: DraftMap): boolean {
    return Object.values(drafts).some(draft => draft.status === DraftStatus.Editing || draft.status === DraftStatus.Dirty || draft.status === DraftStatus.Sending);
}

export function hasDirtyFields(drafts: DraftMap): boolean {
    return Object.values(drafts).some(draft => draft.status === DraftStatus.Dirty);
}

export function hasDirtyFieldsExcludeError(drafts: DraftMap): boolean {
    const now = Date.now();
    return Object.values(drafts).some(
        draft => draft.status === DraftStatus.Dirty &&
            (!draft.errorAt || (now - draft.errorAt > 3000)) // 直近3秒以内にエラーが発生しているフィールドは除外
    );
    // エラー発生時はretryDelay後に再送されるため、即座に再送しないようにする
}

/**
 * フィールド更新管理フック
 */
export function useFieldUpdateManager(opts: {
    patchPageForm: (requestId: string, pageId: string, fields: Record<string, { prev: string, next: string }>) => Promise<EWBCIItpcmdResponse<ItpcmdPatchFormPageResponse>>;
    page?: FormPage;
    initialForm?: { form: Record<string, string>, timestamp?: Date };
    retryDelayMs?: number;
}): FieldUpdateManagerHooks {
    const { patchPageForm, page, initialForm, retryDelayMs = 3000 } = opts;
    const [pageHash, setPageHash] = useState<string>("");
    const { history, addHistory, clearHistory } = useFieldUpdateHistory();
    const [state, dispatch] = useReducer(reducer, initState(addHistory)); // フォームフィールドのドラフト状態を保持するオブジェクトの参照

    /** リトライ間隔を取得する関数 */
    const retryDelay = useCallback(() => {
        // TODO: 将来的に動的に変更できるようにするかも
        return retryDelayMs;
    }, [retryDelayMs]);

    /** 初期化 */
    useEffect(() => {
        if (!page?.id) { setPageHash(""); return; }
        if (!page.fields?.length) { setPageHash(page.id); return; }
        let canceled = false;
        (async () => {
            const newHash = await hashJson(page.fields?.sort(compkey));
            if (!canceled) setPageHash(`${page.id}_${newHash}`);
        })();
        return () => { canceled = true };
    }, [page?.id, page?.fields]);
    const prevHashRef = useRef<string>("");
    useEffect(() => {
        if (pageHash === "" || pageHash === prevHashRef.current) return; // ページが未選択または変更なし

        const logger = Log.getLogger();

        if (isSendingRef.current) {
            // ページ切り替え時に送信中であればフラグをクリア
            isSendingRef.current = null;
            logger.ui.warn('useFieldUpdateManager', 'useEffect(page)', `page changed while sending. cancel sending flag.`);
        }

        dispatch({ type: 'init', pageId: page?.id, fields: page?.fields, initialForm });

        return () => {
            // クリーンアップ時にpageHashを保存
            prevHashRef.current = pageHash;
        };
    }, [pageHash, page?.id, page?.fields, initialForm]);
    /** フォームフィールドにフォーカスが入ったことを通知する */
    const notifyFocusIn = useCallback((key: string) => dispatch({ type: 'focusIn', key }), []);
    /** フォームフィールドからフォーカスが外れたことを通知する */
    const notifyFocusOut = useCallback((key: string, value?: string) => dispatch({ type: 'focusOut', key, value }), []);
    /** eWBから受信したフォームの更新を適用する */
    const notifyRemoteUpdate = useCallback((form: Record<string, string>, timestamp: Date) => dispatch({ type: 'remoteUpdate', form, timestamp }), []);
    /** 競合を解決する */
    const resolveConflict = useCallback((key: string, value: string) => dispatch({ type: 'resolveConflict', key, value }), []);

    useEffect(() => {
        // draftsが更新されDirtyなフィールドがあれば送信をスケジュール
        // const logger = Log.getLogger();
        const pageId = page?.id;
        if (!pageId) return;

        // 送信中でないか確認する
        if (Object.values(state.drafts).some((d: Draft) => d.status === DraftStatus.Sending)) return;

        // Dirtyな全フィールドを取得
        const dirtyEntries = Object.values(state.drafts).filter((d: Draft) => d.status === DraftStatus.Dirty);
        // Dirtyなフィールドがなければ何もしない
        if (dirtyEntries.length === 0) return;

        // 直近3秒以内にエラーが発生しているフィールドしかなければ何もしない
        const now = Date.now();
        if (!dirtyEntries.some((d: Draft) => !d.errorAt || d.errorAt + retryDelay() <= now)) {
            // 送信できるものがない場合は、次の retryAt まで待機し、再評価のためのイベントを発火する
            const nextRetry = Math.min(...dirtyEntries.map((d: Draft) => (d.errorAt! + retryDelay())));
            if (isFinite(nextRetry)) {
                const timer = setTimeout(() => { dispatch({ type: "fireResendEvent" }); }, nextRetry - now);
                return () => clearTimeout(timer);
            }
        }

        // Sendingへ状態を変更
        dispatch({ type: "sendRequest", pageId });

    }, [page?.id, state.drafts, state.retryCount, retryDelay]);

    const isSendingRef = useRef<string | null>(null); // 送信中フラグ（多重送信防止用）
    useEffect(() => {
        // draftsが更新されSendingなフィールドがあれば送信を実行
        const logger = Log.getLogger();
        const pageId = page?.id;
        const targets = Object.values(state.drafts).filter((d: Draft) => d.status === DraftStatus.Sending);
        const requestId = state.requestId;
        if (!pageId             // ページが未選択
            || isSendingRef.current // 既に送信中
            || !targets?.length // 送信対象がなければ何もしない
            || !requestId      // requestIdがなければ何もしない
        ) return;

        // 送信開始
        isSendingRef.current = requestId; // sendingフラグをtrueにする
        const callback = () => { isSendingRef.current = null; }; // 成否問わず、完了イベントで実行する関数

        logger.ui.info('useFieldUpdateManager', 'sendUpdateRequest', `${targets.length} fields request on ${pageId}`);

        // 送信する内容を構築
        const body = targets.reduce((acc, d) => {
            const { key, remote: prev, local: next } = d;
            return { ...acc, [key]: { prev, next } };
        }, {})

        // 非同期で送信する
        patchPageForm(requestId, pageId, body)
            .then((resp) => { // 結果受信
                if (!isSendingRef.current || isSendingRef.current !== requestId) {
                    logger.ui.warn('useFieldUpdateManager', 'sendUpdateRequest', `response received after cancel or new request: [${pageId}] ${requestId}`);
                    return; // 既にキャンセルされているか、新しいリクエストが発行されている場合は無視
                }
                if (!resp || (resp.body?.status !== 200 && resp.body?.status !== 409)) { // 失敗 ※409(競合)は成功として通す
                    throw new Error(`[${resp?.body?.status}] ${resp?.body?.reason}`);
                }
                // 成功
                const responsedAt = resp?.responsedAt ? new Date(resp.responsedAt) : new Date();
                const result: Record<string, string> = resp.body.body?.form ?? {};
                // 状態を更新
                dispatch({ type: "receiveResponse", pageId, requestId, result, responsedAt, callback });
            })
            .catch((err) => {
                if (!isSendingRef.current || isSendingRef.current !== requestId) {
                    logger.ui.warn('useFieldUpdateManager', 'sendUpdateRequest', `response received after cancel or new request: [${pageId}] ${requestId}`);
                    return; // 既にキャンセルされているか、新しいリクエストが発行されている場合は無視
                }
                logger.ui.error('useFieldUpdateManager', 'sendUpdateRequest', `フィールド更新送信エラー: ${errmsg(err)}`);
                // 失敗
                dispatch({ type: "failedToRequest", pageId, requestId, callback });
            });

    }, [page?.id, state.drafts, state.requestId, patchPageForm]);

    const exported = {
        drafts: state.drafts,
        notifyFocusIn,
        notifyFocusOut,
        notifyRemoteUpdate,
        resolveConflict,
        history: history,
        clearHistory,
    };
    /* istanbul ignore if: テスト用のプロパティのためカバレッジを取得しない */
    if (import.meta.env.NODE_ENV !== 'test')
        return exported;
    else
        return {
            ...exported,
            pageHash,
        };
}

export default useFieldUpdateHistory;

/* istanbul ignore next: MT用export指示のためカバレッジを取得しない */
export const __test__ = import.meta.env.NODE_ENV !== 'test' ? {} : { reducer };
